/**
 * Marketing.js - WhatsApp Marketing Campaign Manager
 * 
 * 4 Tabs:
 * 1. Dashboard - Overview stats & recent campaigns
 * 2. Contacts - Upload Excel, manage contact lists
 * 3. Templates - Create & manage Meta templates
 * 4. Campaigns - Create, configure & send campaigns
 * 
 * Location: /home/agentsmatix/message-management/frontend/src/pages/Marketing.js
 * Route: /marketing
 * 
 * @version 1.0.0
 * @date 2026-02-21
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API_BASE = '/api/v1/marketing';

// ============================================================
// TRANSLATIONS (Arabic / English)
// ============================================================
const translations = {
    ar: {
        // Page Header
        pageTitle: 'حملات التسويق',
        pageSubtitle: 'إدارة رسائل التسويق عبر واتساب',

        // Tabs
        tabDashboard: 'لوحة التحكم',
        tabContacts: 'جهات الاتصال',
        tabTemplates: 'القوالب',
        tabCampaigns: 'الحملات',

        // Dashboard
        totalCampaigns: 'إجمالي الحملات',
        messagesSent: 'رسائل مرسلة',
        delivered: 'تم التوصيل',
        read: 'تم القراءة',
        contacts: 'جهات الاتصال',
        approvedTemplates: 'قوالب معتمدة',
        recentCampaigns: 'آخر الحملات',
        thCampaign: 'الحملة',
        thTemplate: 'القالب',
        thStatus: 'الحالة',
        thSent: 'مرسل',
        thDelivered: 'تم التوصيل',
        thRead: 'تم القراءة',
        thFailed: 'فشل',
        thDate: 'التاريخ',
        noCampaignsYet: 'لا توجد حملات بعد',

        // Contacts
        contactLists: 'قوائم جهات الاتصال',
        uploading: 'جاري الرفع...',
        uploadExcel: 'رفع ملف Excel',
        importFromWhatsApp: 'استيراد من واتساب',
        importedSuccess: '✅ تم الاستيراد بنجاح',
        total: 'إجمالي:',
        valid: 'صالح:',
        invalid: 'غير صالح:',
        duplicate: 'مكرر:',
        thListName: 'اسم القائمة',
        thTotal: 'إجمالي',
        thValid: 'صالح',
        thInvalid: 'غير صالح',
        thDuplicate: 'مكرر',
        thActions: 'إجراءات',
        uploadExcelToAdd: 'قم برفع ملف Excel لإضافة جهات اتصال',
        searchByNameOrPhone: 'بحث بالاسم أو الرقم...',
        deleteSelected: 'حذف المحدد',
        numberCount: 'رقم',
        thName: 'الاسم',
        thWhatsappNumber: 'رقم الواتساب',
        thCompany: 'الشركة',
        validStatus: '✓ صالح',
        invalidStatus: '✗ غير صالح',
        noResults: 'لا توجد نتائج',
        previous: '‹ السابق',
        next: 'التالي ›',
        pageOf: 'صفحة {page} من {total}',
        contactsOf: 'جهات اتصال:',
        promptListName: 'اسم القائمة (مثال: عملاء Q1 2026):',
        promptListDefault: 'Import',
        importedContacts: 'تم استيراد {count} جهة اتصال بنجاح',
        fileUploadError: 'خطأ في رفع الملف',
        confirmDeleteList: 'هل أنت متأكد من حذف هذه القائمة؟',
        listDeleted: 'تم حذف القائمة',
        confirmDeleteNumber: 'حذف هذا الرقم؟',
        deleted: 'تم الحذف',
        confirmBulkDelete: 'حذف {count} رقم؟',
        bulkDeleted: 'تم حذف {count} رقم',
        updated: 'تم التحديث',
        edit: 'تعديل',
        delete: 'حذف',

        // Templates
        whatsappTemplates: 'قوالب واتساب',
        syncing: 'جاري المزامنة...',
        syncFromMeta: 'مزامنة من Meta',
        createNewTemplate: 'إنشاء قالب جديد',
        syncedTemplates: 'تم مزامنة {count} قالب من Meta',
        templateNameAndBodyRequired: 'اسم القالب والنص الأساسي مطلوبان',
        templateCreatedForReview: 'تم إنشاء القالب وإرساله للمراجعة',
        templateCreateError: 'خطأ في إنشاء القالب',
        confirmDeleteTemplate: 'هل أنت متأكد؟ سيتم حذف القالب من Meta أيضاً',
        templateDeleted: 'تم حذف القالب',
        arabicLang: '🇪🇬 عربي',
        englishLang: '🇬🇧 English',
        buttonsYes: 'أزرار ✓',
        paramCount: 'بارامتر',
        noTemplates: 'لا توجد قوالب - قم بالمزامنة من Meta أو أنشئ قالب جديد',
        noBodyText: 'No body text',

        // Create Template Modal
        createTemplateTitle: 'إنشاء قالب جديد',
        templateName: 'اسم القالب *',
        templateNamePlaceholder: 'مثال: marketing_offer_2026',
        templateNameHint: 'حروف إنجليزية صغيرة و _ فقط',
        language: 'اللغة',
        langArabic: '🇪🇬 العربية',
        langEnglish: '🇬🇧 English',
        langEnglishUS: '🇺🇸 English (US)',
        category: 'الفئة',
        catMarketing: 'تسويق (Marketing)',
        catUtility: 'خدمي (Utility)',
        headerType: 'نوع الهيدر',
        headerNone: 'بدون هيدر',
        headerText: 'نص',
        headerImage: 'صورة',
        headerVideo: 'فيديو',
        headerDocument: 'مستند',
        headerTextPlaceholder: 'نص الهيدر (60 حرف كحد أقصى)',
        mediaUrlPlaceholder: 'URL مثال للوسائط أو ارفع ملف',
        uploadBtn: '📁 رفع',
        maxSize5MB: 'الحد الأقصى 5 ميجا',
        fileUploaded: 'تم رفع الملف ✅',
        uploadFailed: 'فشل الرفع',
        uploadError: 'خطأ في الرفع',
        messageBody: 'نص الرسالة * (استخدم {{1}}, {{2}} للمتغيرات)',
        messageBodyPlaceholder: 'مرحباً {{1}}! 🎉\n\nنحن في {{2}} نقدم لك عرض خاص...\n\nتواصل معنا الآن!',
        exampleValues: 'أمثلة القيم (مفصولة بفاصلة)',
        exampleValuesPlaceholder: 'أحمد, شركة ماتيكس, خصم 50%',
        paramLabels: 'تسميات البارامترات (اختياري)',
        paramLabelsPlaceholder: 'اسم العميل, اسم الشركة, العرض',
        footerText: 'نص الفوتر (اختياري)',
        footerPlaceholder: 'مثال: لإلغاء الاشتراك أرسل STOP',
        buttonsLabel: 'الأزرار (حتى 3 أزرار)',
        addButton: 'إضافة زر',
        quickReply: 'رد سريع',
        urlType: 'رابط',
        phoneType: 'رقم هاتف',
        buttonTextPlaceholder: 'نص الزر',
        cancel: 'إلغاء',
        creating: 'جاري الإنشاء...',
        createAndSubmit: 'إنشاء وإرسال للمراجعة',

        // Campaigns
        marketingCampaigns: 'حملات التسويق',
        newCampaign: 'حملة جديدة',
        templateLabel: 'قالب:',
        listLabel: 'قائمة:',
        contactUnit: 'جهة اتصال',
        sent: 'مرسل',
        failed: 'فشل',
        startSending: 'بدء الإرسال',
        pauseCampaign: 'إيقاف مؤقت',
        resumeCampaign: 'استئناف',
        cancelCampaign: 'إلغاء',
        deleteCampaign: 'حذف',
        confirmDeleteCampaign: 'هل أنت متأكد من حذف هذه الحملة؟',
        campaignDeleted: 'تم حذف الحملة',
        cannotDeleteSending: 'لا يمكن حذف حملة قيد الإرسال',
        details: 'تفاصيل',
        noCampaignsCreate: 'لا توجد حملات بعد - أنشئ حملة جديدة',
        confirmStart: 'هل أنت متأكد من بدء إرسال الحملة؟',
        confirmPause: 'هل تريد إيقاف الحملة مؤقتاً؟',
        confirmResume: 'هل تريد استئناف الحملة؟',
        confirmCancel: 'هل أنت متأكد من إلغاء الحملة؟ لا يمكن التراجع عن هذا.',
        campaignStarted: 'تم بدء الحملة',
        campaignPaused: 'تم إيقاف الحملة',
        campaignResumed: 'تم استئناف الحملة',
        campaignCancelled: 'تم إلغاء الحملة',
        fillRequiredFields: 'يرجى ملء جميع الحقول المطلوبة',
        campaignCreated: 'تم إنشاء الحملة',

        // Create Campaign Modal
        createCampaignTitle: 'إنشاء حملة جديدة',
        campaignName: 'اسم الحملة *',
        campaignNamePlaceholder: 'مثال: حملة رمضان 2026',
        selectTemplate: 'القالب *',
        selectTemplatePlaceholder: 'اختر قالب...',
        noApprovedTemplates: '⚠ لا توجد قوالب معتمدة. يرجى انتظار الموافقة من Meta',
        selectContactList: 'قائمة جهات الاتصال *',
        selectListPlaceholder: 'اختر قائمة...',
        notifyAgents: 'موظفين الإشعارات (اختياري)',
        notifyAgentsDesc: 'اختر الموظفين الذين سيصلهم إشعار عند تفاعل العميل مع الحملة',
        imageLink: '🖼️ رابط الصورة',
        videoLink: '🎥 رابط الفيديو',
        fileLink: '📄 رابط الملف',
        enterImageUrl: 'أدخل رابط الصورة الذي سيظهر في رأس الرسالة',
        enterVideoUrl: 'أدخل رابط الفيديو الذي سيظهر في رأس الرسالة',
        enterFileUrl: 'أدخل رابط الملف الذي سيظهر في رأس الرسالة',
        imageUrlPlaceholder: 'https://example.com/image.jpg أو ارفع صورة',
        genericUrlPlaceholder: 'https://example.com/file',
        uploadFile: '📁 رفع ملف',
        fileUploadedSuccess: 'تم رفع الملف بنجاح ✅',
        paramMapping: 'ربط البارامترات',
        templateText: 'نص القالب:',
        paramLabel: 'بارامتر {n}',
        selectFieldOrCustom: 'اختر حقل أو أدخل قيمة ثابتة...',
        customerName: 'اسم العميل',
        companyName: 'اسم الشركة',
        phoneNumber: 'رقم الهاتف',
        customValue: 'قيمة ثابتة',
        enterCustomValue: 'أدخل القيمة الثابتة',
        safetySettings: '⚙️ إعدادات الأمان والإرسال',
        batchSize: 'حجم الدفعة (batch size)',
        batchDelay: 'التأخير بين الدفعات (ثانية)',
        maxPerHour: 'الحد الأقصى في الساعة',
        businessHoursOnly: 'ساعات العمل فقط',
        from: 'من',
        to: 'إلى',
        createCampaign: 'إنشاء الحملة',

        // Campaign Detail Modal
        detailsTitle: 'تفاصيل:',
        activityLog: 'سجل النشاط',
        recipientsList: 'قائمة المستلمين',
        phoneNumber: 'رقم الهاتف',
        contactName: 'اسم جهة الاتصال',
        messageStatus: 'الحالة',
        sentAt: 'وقت الإرسال',
        deliveredAt: 'وقت التوصيل',
        readAt: 'وقت القراءة',
        allStatuses: 'الكل',
        campaignInfo: 'معلومات الحملة',
        templateUsed: 'القالب المستخدم',
        contactList: 'قائمة جهات الاتصال',
        createdBy: 'أنشئت بواسطة',
        startedAt: 'بدأت في',
        completedAt: 'اكتملت في',
        totalRecipients: 'إجمالي المستلمين',
        backToCampaigns: '→ رجوع للحملات',

        // Status badges
        statusDraft: 'مسودة',
        statusScheduled: 'مجدول',
        statusSending: 'جاري الإرسال...',
        statusPaused: 'متوقف',
        statusCompleted: 'مكتمل',
        statusCancelled: 'ملغي',
        statusFailed: 'فشل',
        statusPending: 'قيد المراجعة',
        statusApproved: 'معتمد',
        statusRejected: 'مرفوض',
        statusDisabled: 'معطل',
    },
    en: {
        // Page Header
        pageTitle: 'Marketing Campaigns',
        pageSubtitle: 'Manage WhatsApp marketing messages',

        // Tabs
        tabDashboard: 'Dashboard',
        tabContacts: 'Contacts',
        tabTemplates: 'Templates',
        tabCampaigns: 'Campaigns',

        // Dashboard
        totalCampaigns: 'Total Campaigns',
        messagesSent: 'Messages Sent',
        delivered: 'Delivered',
        read: 'Read',
        contacts: 'Contacts',
        approvedTemplates: 'Approved Templates',
        recentCampaigns: 'Recent Campaigns',
        thCampaign: 'Campaign',
        thTemplate: 'Template',
        thStatus: 'Status',
        thSent: 'Sent',
        thDelivered: 'Delivered',
        thRead: 'Read',
        thFailed: 'Failed',
        thDate: 'Date',
        noCampaignsYet: 'No campaigns yet',

        // Contacts
        contactLists: 'Contact Lists',
        uploading: 'Uploading...',
        uploadExcel: 'Upload Excel File',
        importFromWhatsApp: 'Import from WhatsApp',
        importedSuccess: '✅ Import Successful',
        total: 'Total:',
        valid: 'Valid:',
        invalid: 'Invalid:',
        duplicate: 'Duplicate:',
        thListName: 'List Name',
        thTotal: 'Total',
        thValid: 'Valid',
        thInvalid: 'Invalid',
        thDuplicate: 'Duplicate',
        thActions: 'Actions',
        uploadExcelToAdd: 'Upload an Excel file to add contacts',
        searchByNameOrPhone: 'Search by name or number...',
        deleteSelected: 'Delete Selected',
        numberCount: 'numbers',
        thName: 'Name',
        thWhatsappNumber: 'WhatsApp Number',
        thCompany: 'Company',
        validStatus: '✓ Valid',
        invalidStatus: '✗ Invalid',
        noResults: 'No results found',
        previous: '‹ Previous',
        next: 'Next ›',
        pageOf: 'Page {page} of {total}',
        contactsOf: 'Contacts:',
        promptListName: 'List name (e.g. Clients Q1 2026):',
        promptListDefault: 'Import',
        importedContacts: 'Successfully imported {count} contacts',
        fileUploadError: 'Error uploading file',
        confirmDeleteList: 'Are you sure you want to delete this list?',
        listDeleted: 'List deleted',
        confirmDeleteNumber: 'Delete this number?',
        deleted: 'Deleted',
        confirmBulkDelete: 'Delete {count} numbers?',
        bulkDeleted: '{count} numbers deleted',
        updated: 'Updated',
        edit: 'Edit',
        delete: 'Delete',

        // Templates
        whatsappTemplates: 'WhatsApp Templates',
        syncing: 'Syncing...',
        syncFromMeta: 'Sync from Meta',
        createNewTemplate: 'Create New Template',
        syncedTemplates: '{count} templates synced from Meta',
        templateNameAndBodyRequired: 'Template name and body text are required',
        templateCreatedForReview: 'Template created and submitted for review',
        templateCreateError: 'Error creating template',
        confirmDeleteTemplate: 'Are you sure? Template will also be deleted from Meta',
        templateDeleted: 'Template deleted',
        arabicLang: '🇪🇬 Arabic',
        englishLang: '🇬🇧 English',
        buttonsYes: 'Buttons ✓',
        paramCount: 'params',
        noTemplates: 'No templates - Sync from Meta or create a new template',
        noBodyText: 'No body text',

        // Create Template Modal
        createTemplateTitle: 'Create New Template',
        templateName: 'Template Name *',
        templateNamePlaceholder: 'e.g. marketing_offer_2026',
        templateNameHint: 'Lowercase English letters and _ only',
        language: 'Language',
        langArabic: '🇪🇬 Arabic',
        langEnglish: '🇬🇧 English',
        langEnglishUS: '🇺🇸 English (US)',
        category: 'Category',
        catMarketing: 'Marketing',
        catUtility: 'Utility',
        headerType: 'Header Type',
        headerNone: 'No Header',
        headerText: 'Text',
        headerImage: 'Image',
        headerVideo: 'Video',
        headerDocument: 'Document',
        headerTextPlaceholder: 'Header text (max 60 characters)',
        mediaUrlPlaceholder: 'Example media URL or upload file',
        uploadBtn: '📁 Upload',
        maxSize5MB: 'Max file size 5MB',
        fileUploaded: 'File uploaded ✅',
        uploadFailed: 'Upload failed',
        uploadError: 'Upload error',
        messageBody: 'Message Body * (use {{1}}, {{2}} for variables)',
        messageBodyPlaceholder: 'Hello {{1}}! 🎉\n\nWe at {{2}} have a special offer for you...\n\nContact us now!',
        exampleValues: 'Example values (comma-separated)',
        exampleValuesPlaceholder: 'Ahmed, Matix Company, 50% off',
        paramLabels: 'Parameter labels (optional)',
        paramLabelsPlaceholder: 'Customer name, Company name, Offer',
        footerText: 'Footer text (optional)',
        footerPlaceholder: 'e.g. Send STOP to unsubscribe',
        buttonsLabel: 'Buttons (up to 3)',
        addButton: 'Add Button',
        quickReply: 'Quick Reply',
        urlType: 'URL',
        phoneType: 'Phone Number',
        buttonTextPlaceholder: 'Button text',
        cancel: 'Cancel',
        creating: 'Creating...',
        createAndSubmit: 'Create & Submit for Review',

        // Campaigns
        marketingCampaigns: 'Marketing Campaigns',
        newCampaign: 'New Campaign',
        templateLabel: 'Template:',
        listLabel: 'List:',
        contactUnit: 'contacts',
        sent: 'Sent',
        failed: 'Failed',
        startSending: 'Start Sending',
        pauseCampaign: 'Pause',
        resumeCampaign: 'Resume',
        cancelCampaign: 'Cancel',
        deleteCampaign: 'Delete',
        confirmDeleteCampaign: 'Are you sure you want to delete this campaign?',
        campaignDeleted: 'Campaign deleted',
        cannotDeleteSending: 'Cannot delete a campaign that is currently sending',
        details: 'Details',
        noCampaignsCreate: 'No campaigns yet - Create a new campaign',
        confirmStart: 'Are you sure you want to start sending the campaign?',
        confirmPause: 'Do you want to pause the campaign?',
        confirmResume: 'Do you want to resume the campaign?',
        confirmCancel: 'Are you sure you want to cancel the campaign? This cannot be undone.',
        campaignStarted: 'Campaign started',
        campaignPaused: 'Campaign paused',
        campaignResumed: 'Campaign resumed',
        campaignCancelled: 'Campaign cancelled',
        fillRequiredFields: 'Please fill all required fields',
        campaignCreated: 'Campaign created',

        // Create Campaign Modal
        createCampaignTitle: 'Create New Campaign',
        campaignName: 'Campaign Name *',
        campaignNamePlaceholder: 'e.g. Ramadan Campaign 2026',
        selectTemplate: 'Template *',
        selectTemplatePlaceholder: 'Select template...',
        noApprovedTemplates: '⚠ No approved templates. Please wait for Meta approval',
        selectContactList: 'Contact List *',
        selectListPlaceholder: 'Select list...',
        notifyAgents: 'Notification Agents (optional)',
        notifyAgentsDesc: 'Select agents to be notified when a customer interacts with the campaign',
        imageLink: '🖼️ Image URL',
        videoLink: '🎥 Video URL',
        fileLink: '📄 File URL',
        enterImageUrl: 'Enter the image URL to appear in the message header',
        enterVideoUrl: 'Enter the video URL to appear in the message header',
        enterFileUrl: 'Enter the file URL to appear in the message header',
        imageUrlPlaceholder: 'https://example.com/image.jpg or upload image',
        genericUrlPlaceholder: 'https://example.com/file',
        uploadFile: '📁 Upload File',
        fileUploadedSuccess: 'File uploaded successfully ✅',
        paramMapping: 'Parameter Mapping',
        templateText: 'Template text:',
        paramLabel: 'Parameter {n}',
        selectFieldOrCustom: 'Select field or enter custom value...',
        customerName: 'Customer Name',
        companyName: 'Company Name',
        phoneNumber: 'Phone Number',
        customValue: 'Custom Value',
        enterCustomValue: 'Enter custom value',
        safetySettings: '⚙️ Safety & Sending Settings',
        batchSize: 'Batch Size',
        batchDelay: 'Delay Between Batches (sec)',
        maxPerHour: 'Max Per Hour',
        businessHoursOnly: 'Business Hours Only',
        from: 'From',
        to: 'To',
        createCampaign: 'Create Campaign',

        // Campaign Detail Modal
        detailsTitle: 'Details:',
        activityLog: 'Activity Log',
        recipientsList: 'Recipients List',
        phoneNumber: 'Phone Number',
        contactName: 'Contact Name',
        messageStatus: 'Status',
        sentAt: 'Sent At',
        deliveredAt: 'Delivered At',
        readAt: 'Read At',
        allStatuses: 'All',
        campaignInfo: 'Campaign Info',
        templateUsed: 'Template Used',
        contactList: 'Contact List',
        createdBy: 'Created By',
        startedAt: 'Started At',
        completedAt: 'Completed At',
        totalRecipients: 'Total Recipients',
        backToCampaigns: '← Back to Campaigns',

        // Status badges
        statusDraft: 'Draft',
        statusScheduled: 'Scheduled',
        statusSending: 'Sending...',
        statusPaused: 'Paused',
        statusCompleted: 'Completed',
        statusCancelled: 'Cancelled',
        statusFailed: 'Failed',
        statusPending: 'Pending Review',
        statusApproved: 'Approved',
        statusRejected: 'Rejected',
        statusDisabled: 'Disabled',
    }
};

// ============================================================
// API Helper
// ============================================================
const api = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            ...(!options.isFormData && { 'Content-Type': 'application/json' }),
        },
        ...options
    };
    if (options.isFormData) {
        delete config.headers['Content-Type'];
    }
    const response = await fetch(`${API_BASE}${endpoint}`, config);
    return response.json();
};

// ============================================================
// ICONS (inline SVG to avoid dependencies)
// ============================================================
const Icons = {
    Dashboard: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    Contacts: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
    Template: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
    Campaign: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>,
    Upload: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
    Sync: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>,
    Play: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
    Pause: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
    Stop: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>,
    Plus: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>,
    Eye: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    Check: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
    Warning: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    WhatsApp: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
    Close: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
};

// ============================================================
// STATUS BADGE Component
// ============================================================
const StatusBadge = ({ status, t }) => {
    const config = {
        draft: { bg: 'rgba(107,114,128,0.15)', color: '#9ca3af', label: t?.statusDraft || 'Draft' },
        scheduled: { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa', label: t?.statusScheduled || 'Scheduled' },
        sending: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', label: t?.statusSending || 'Sending...' },
        paused: { bg: 'rgba(249,115,22,0.15)', color: '#fb923c', label: t?.statusPaused || 'Paused' },
        completed: { bg: 'rgba(16,185,129,0.15)', color: '#34d399', label: t?.statusCompleted || 'Completed' },
        cancelled: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', label: t?.statusCancelled || 'Cancelled' },
        failed: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', label: t?.statusFailed || 'Failed' },
        PENDING: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', label: t?.statusPending || 'Pending Review' },
        APPROVED: { bg: 'rgba(16,185,129,0.15)', color: '#34d399', label: t?.statusApproved || 'Approved' },
        REJECTED: { bg: 'rgba(239,68,68,0.15)', color: '#f87171', label: t?.statusRejected || 'Rejected' },
        PAUSED: { bg: 'rgba(249,115,22,0.15)', color: '#fb923c', label: t?.statusPaused || 'Paused' },
        DISABLED: { bg: 'rgba(107,114,128,0.15)', color: '#9ca3af', label: t?.statusDisabled || 'Disabled' },
    };
    const c = config[status] || config.draft;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', padding: '4px 12px',
            borderRadius: '20px', fontSize: '12px', fontWeight: 600,
            background: c.bg, color: c.color, border: `1px solid ${c.color}33`,
            letterSpacing: '0.02em'
        }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color, marginInlineStart: 6 }}/>
            {c.label}
        </span>
    );
};

// ============================================================
// STAT CARD Component
// ============================================================
const StatCard = ({ icon, label, value, color, subtext }) => (
    <div style={{
        background: 'var(--bg-card, rgba(26,32,56,0.6))',
        border: '1px solid var(--border-primary, rgba(99,102,241,0.15))',
        borderRadius: '16px', padding: '24px',
        display: 'flex', flexDirection: 'column', gap: '8px',
        transition: 'all 0.3s', cursor: 'default',
        position: 'relative', overflow: 'hidden'
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 0 20px ${color}33`; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-primary, rgba(99,102,241,0.15))'; e.currentTarget.style.boxShadow = 'none'; }}
    >
        <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `${color}08` }}/>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-tertiary, #9ca3af)', fontSize: '13px' }}>
            <span style={{ color }}>{icon}</span>
            {label}
        </div>
        <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-primary, #fff)', letterSpacing: '-0.02em' }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {subtext && <div style={{ fontSize: '12px', color: 'var(--text-tertiary, #6b7280)' }}>{subtext}</div>}
    </div>
);

// ============================================================
// MODAL Component
// ============================================================
const Modal = ({ isOpen, onClose, title, children, width = '600px' }) => {
    if (!isOpen) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)'
        }} onClick={onClose}>
            <div style={{
                background: 'var(--bg-card, #1a2038)', border: '1px solid var(--border-primary, rgba(99,102,241,0.2))',
                borderRadius: '20px', width, maxWidth: '95vw', maxHeight: '90vh',
                overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
            }} onClick={e => e.stopPropagation()}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '24px 28px', borderBottom: '1px solid var(--border-primary, rgba(99,102,241,0.15))'
                }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary, #fff)' }}>{title}</h3>
                    <button onClick={onClose} style={{
                        background: 'rgba(255,255,255,0.05)', border: 'none',
                        color: 'var(--text-secondary, #9ca3af)', cursor: 'pointer',
                        width: 36, height: 36, borderRadius: '10px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}><Icons.Close/></button>
                </div>
                <div style={{ padding: '28px' }}>{children}</div>
            </div>
        </div>
    );
};

// ============================================================
// FORM STYLES (shared)
// ============================================================
const inputStyle = {
    width: '100%', padding: '12px 16px',
    background: 'var(--bg-tertiary, rgba(255,255,255,0.05))',
    border: '1px solid var(--border-primary, rgba(99,102,241,0.15))',
    borderRadius: '10px', color: 'var(--text-primary, #fff)',
    fontSize: '14px', outline: 'none', transition: 'border-color 0.2s'
};

const labelStyle = {
    display: 'block', marginBottom: '6px',
    fontSize: '13px', fontWeight: 600,
    color: 'var(--text-secondary, #9ca3af)'
};

const btnPrimary = {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#fff', border: 'none', borderRadius: '10px',
    padding: '12px 24px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px',
    transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(99,102,241,0.3)'
};

const btnSecondary = {
    background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary, #fff)',
    border: '1px solid var(--border-primary, rgba(99,102,241,0.15))',
    borderRadius: '10px', padding: '10px 20px', fontSize: '14px',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px',
    transition: 'all 0.2s'
};

const btnDanger = {
    ...btnSecondary,
    color: '#f87171', borderColor: 'rgba(239,68,68,0.3)'
};

const btnSuccess = {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff', border: 'none', borderRadius: '10px',
    padding: '12px 24px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px'
};

const btnWarning = {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: '#fff', border: 'none', borderRadius: '10px',
    padding: '10px 20px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px'
};


// ============================================================
// MAIN COMPONENT
// ============================================================
const Marketing = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
    const [loading, setLoading] = useState(false);
    const [agentsList, setAgentsList] = useState([]);
    const [toast, setToast] = useState(null);

    // Language support from global context
    const { lang } = useLanguage();
    const t = translations[lang] || translations.ar;
    const isRTL = lang === 'ar';
    const dir = isRTL ? 'rtl' : 'ltr';
    const dateLocale = isRTL ? 'ar-EG' : 'en-US';

    // Dashboard
    const [dashboardData, setDashboardData] = useState(null);
    
    // Contacts
    const [contactLists, setContactLists] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [selectedList, setSelectedList] = useState(null);
    const [listContacts, setListContacts] = useState([]);

    // Templates
    const [templates, setTemplates] = useState([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Campaigns
    const [campaigns, setCampaigns] = useState([]);
    const [showCampaignModal, setShowCampaignModal] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState(new Set());
    const [editingContact, setEditingContact] = useState(null);
    const [contactSearch, setContactSearch] = useState('');
    const [contactPage, setContactPage] = useState(1);
    const [contactPagination, setContactPagination] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [campaignDetail, setCampaignDetail] = useState(null);

    const fileInputRef = useRef(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    // ============================================================
    // DATA FETCHING
    // ============================================================
    const fetchDashboard = useCallback(async () => {
        const data = await api('/dashboard');
        if (data.success) setDashboardData(data);
    }, []);

    const fetchContactLists = useCallback(async () => {
        const data = await api('/contacts/lists');
        if (data.success) setContactLists(data.lists);
    }, []);

    const fetchTemplates = useCallback(async () => {
        const data = await api('/templates');
        if (data.success) setTemplates(data.templates);
    }, []);

    const fetchCampaigns = useCallback(async () => {
        const data = await api('/campaigns');
        if (data.success) setCampaigns(data.campaigns);
    }, []);

    const fetchAgents = useCallback(async () => {
        try {
            const res = await fetch("/api/v1/agents", { headers: { "Authorization": "Bearer " + localStorage.getItem("token") } });
            const data = await res.json();
            if (data.success) setAgentsList(data.data || data.agents || []);
        } catch(e) { console.error(e); }
    }, []);
    useEffect(() => {
        if (activeTab === 'dashboard') fetchDashboard();
        if (activeTab === 'contacts') fetchContactLists();
        if (activeTab === 'templates') fetchTemplates();
        if (activeTab === 'campaigns') { fetchCampaigns(); fetchTemplates(); fetchContactLists(); fetchAgents(); }
    }, [activeTab, fetchDashboard, fetchContactLists, fetchTemplates, fetchCampaigns]);

    // Auto-refresh for active campaigns
    useEffect(() => {
        if (activeTab === 'campaigns' || activeTab === 'dashboard') {
            const interval = setInterval(() => {
                if (activeTab === 'campaigns') fetchCampaigns();
                if (activeTab === 'dashboard') fetchDashboard();
            }, 10000);
            return () => clearInterval(interval);
        }
    }, [activeTab, fetchCampaigns, fetchDashboard]);

    // ============================================================
    // CONTACT UPLOAD
    // ============================================================
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const listName = prompt(t.promptListName, `${t.promptListDefault} ${new Date().toLocaleDateString(dateLocale)}`);
        if (!listName) return;

        setLoading(true);
        setUploadResult(null);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('list_name', listName);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/contacts/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await response.json();
            if (data.success) {
                setUploadResult(data);
                showToast(t.importedContacts.replace('{count}', data.stats.valid));
                fetchContactLists();
            } else {
                showToast(data.error || data.message, 'error');
            }
        } catch (err) {
            showToast(t.fileUploadError, 'error');
        }
        setLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const deleteList = async (id) => {
        if (!window.confirm(t.confirmDeleteList)) return;
        const data = await api(`/contacts/lists/${id}`, { method: 'DELETE' });
        if (data.success) {
            showToast(t.listDeleted);
            fetchContactLists();
        } else {
            showToast(data.message || data.error, 'error');
        }
    };

    const viewListContacts = (list) => {
        navigate(`/marketing/contacts/${list.id}`);
    };

    const deleteContact = async (id) => {
        if (!window.confirm(t.confirmDeleteNumber)) return;
        const data = await api(`/contacts/${id}`, { method: 'DELETE' });
        if (data.success) {
            setListContacts(prev => prev.filter(c => c.id !== id));
            selectedContacts.delete(id);
            setSelectedContacts(new Set(selectedContacts));
            showToast(t.deleted);
        }
    };

    const bulkDeleteContacts = async () => {
        if (!selectedContacts.size) return;
        if (!window.confirm(t.confirmBulkDelete.replace('{count}', selectedContacts.size))) return;
        const data = await api('/contacts/bulk-delete', {
            method: 'POST',
            body: JSON.stringify({ ids: [...selectedContacts] })
        });
        if (data.success) {
            showToast(t.bulkDeleted.replace('{count}', data.deleted));
            viewListContacts(selectedList, contactPage);
            fetchContactLists();
        }
    };

    const updateContact = async (id, updates) => {
        const data = await api(`/contacts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
        });
        if (data.success) {
            setListContacts(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
            setEditingContact(null);
            showToast(t.updated);
        }
    };

    const toggleContactSelect = (id) => {
        const next = new Set(selectedContacts);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedContacts(next);
    };

    const toggleAllContacts = () => {
        if (selectedContacts.size === filteredListContacts.length) {
            setSelectedContacts(new Set());
        } else {
            setSelectedContacts(new Set(filteredListContacts.map(c => c.id)));
        }
    };

    const filteredListContacts = listContacts.filter(c => {
        if (!contactSearch) return true;
        return (c.name || '').includes(contactSearch) || (c.phone || '').includes(contactSearch) || (c.company_name || '').includes(contactSearch);
    });

    // ============================================================
    // TEMPLATE MANAGEMENT
    // ============================================================
    const syncTemplates = async () => {
        setSyncing(true);
        const data = await api('/templates/sync', { method: 'POST' });
        if (data.success) {
            showToast(t.syncedTemplates.replace('{count}', data.synced));
            fetchTemplates();
        } else {
            showToast(data.error, 'error');
        }
        setSyncing(false);
    };

    const [templateForm, setTemplateForm] = useState({
        name: '', language: 'ar', category: 'MARKETING',
        header_type: 'NONE', header_text: '', header_example_url: '',
        body_text: '', body_example_values: '',
        body_param_labels: '',
        footer_text: '',
        buttons: []
    });

    const createTemplate = async () => {
        if (!templateForm.name || !templateForm.body_text) {
            showToast(t.templateNameAndBodyRequired, 'error');
            return;
        }

        setLoading(true);
        const payload = {
            name: templateForm.name.toLowerCase().replace(/\s+/g, '_'),
            language: templateForm.language,
            category: templateForm.category,
            header: templateForm.header_type !== 'NONE' ? {
                type: templateForm.header_type,
                text: templateForm.header_type === 'TEXT' ? templateForm.header_text : undefined,
                example_url: ['IMAGE','VIDEO','DOCUMENT'].includes(templateForm.header_type) ? templateForm.header_example_url : undefined
            } : undefined,
            body: {
                text: templateForm.body_text,
                example_values: templateForm.body_example_values ? templateForm.body_example_values.split(',').map(v => v.trim()) : [],
                param_labels: templateForm.body_param_labels ? templateForm.body_param_labels.split(',').map(v => v.trim()) : []
            },
            footer: templateForm.footer_text ? { text: templateForm.footer_text } : undefined,
            buttons: templateForm.buttons.length > 0 ? templateForm.buttons : undefined
        };

        const data = await api('/templates/create', {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        if (data.success) {
            showToast(t.templateCreatedForReview);
            setShowTemplateModal(false);
            setTemplateForm({
                name: '', language: 'ar', category: 'MARKETING',
                header_type: 'NONE', header_text: '', header_example_url: '',
                body_text: '', body_example_values: '', body_param_labels: '',
                footer_text: '', buttons: []
            });
            fetchTemplates();
        } else {
            showToast(data.error || t.templateCreateError, 'error');
        }
        setLoading(false);
    };

    const addButton = () => {
        if (templateForm.buttons.length >= 3) return;
        setTemplateForm(prev => ({
            ...prev,
            buttons: [...prev.buttons, { type: 'QUICK_REPLY', text: '' }]
        }));
    };

    const updateButton = (index, field, value) => {
        setTemplateForm(prev => {
            const buttons = [...prev.buttons];
            buttons[index] = { ...buttons[index], [field]: value };
            return { ...prev, buttons };
        });
    };

    const removeButton = (index) => {
        setTemplateForm(prev => ({
            ...prev,
            buttons: prev.buttons.filter((_, i) => i !== index)
        }));
    };

    const deleteTemplate = async (id) => {
        if (!window.confirm(t.confirmDeleteTemplate)) return;
        const data = await api(`/templates/${id}`, { method: 'DELETE' });
        if (data.success) {
            showToast(t.templateDeleted);
            fetchTemplates();
        } else {
            showToast(data.error, 'error');
        }
    };

    // ============================================================
    // CAMPAIGN MANAGEMENT
    // ============================================================
    const [campaignForm, setCampaignForm] = useState({
        name: '', description: '', template_id: '', contact_list_id: '',
        batch_size: 10, batch_delay_seconds: 5,
        max_per_hour: 200, respect_business_hours: true,
        business_hours_start: '09:00', business_hours_end: '21:00',
        param_mapping: {}, custom_params: {}, notify_agents: [], header_media_url: ''
    });

    const createCampaign = async () => {
        if (!campaignForm.name || !campaignForm.template_id || !campaignForm.contact_list_id) {
            showToast(t.fillRequiredFields, 'error');
            return;
        }
        setLoading(true);
        const data = await api('/campaigns', {
            method: 'POST',
            body: JSON.stringify(campaignForm)
        });
        if (data.success) {
            showToast(t.campaignCreated);
            setShowCampaignModal(false);
            fetchCampaigns();
        } else {
            showToast(data.error || data.message, 'error');
        }
        setLoading(false);
    };

    const campaignAction = async (id, action) => {
        const confirmMessages = {
            start: t.confirmStart,
            pause: t.confirmPause,
            resume: t.confirmResume,
            cancel: t.confirmCancel
        };
        if (!window.confirm(confirmMessages[action])) return;
        
        const data = await api(`/campaigns/${id}/${action}`, { method: 'POST' });
        if (data.success) {
            const actionMsg = { start: t.campaignStarted, pause: t.campaignPaused, resume: t.campaignResumed, cancel: t.campaignCancelled };
            showToast(actionMsg[action]);
            fetchCampaigns();
        } else {
            showToast(data.error, 'error');
        }
    };

    const [campaignMessages, setCampaignMessages] = useState([]);
    const [messageFilter, setMessageFilter] = useState('');

    const viewCampaignDetail = async (campaign) => {
        setSelectedCampaign(campaign);
        setMessageFilter('');
        const data = await api(`/campaigns/${campaign.id}`);
        if (data.success) setCampaignDetail(data);
        const msgData = await api(`/campaigns/${campaign.id}/messages?limit=500`);
        if (msgData.success) setCampaignMessages(msgData.messages || []);
    };

    const deleteCampaign = async (id, status) => {
        if (status === 'sending') { showToast(t.cannotDeleteSending, 'error'); return; }
        if (!window.confirm(t.confirmDeleteCampaign)) return;
        const data = await api(`/campaigns/${id}`, { method: 'DELETE' });
        if (data.success) {
            showToast(t.campaignDeleted);
            fetchCampaigns();
            fetchDashboard();
        } else {
            showToast(data.error || data.message, 'error');
        }
    };

    // ============================================================
    // RENDER TABS
    // ============================================================
    const tabs = [
        { key: 'dashboard', label: t.tabDashboard, icon: <Icons.Dashboard/> },
        { key: 'contacts', label: t.tabContacts, icon: <Icons.Contacts/> },
        { key: 'templates', label: t.tabTemplates, icon: <Icons.Template/> },
        { key: 'campaigns', label: t.tabCampaigns, icon: <Icons.Campaign/> },
    ];

    // ============================================================
    // DASHBOARD TAB
    // ============================================================
    const renderDashboard = () => {
        const s = dashboardData?.stats || {};
        return (
            <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px', marginBottom: '32px' }}>
                    <StatCard icon={<Icons.Campaign/>} label={t.totalCampaigns} value={s.total_campaigns || 0} color="#6366f1"/>
                    <StatCard icon={<Icons.WhatsApp/>} label={t.messagesSent} value={s.total_sent || 0} color="#25d366"/>
                    <StatCard icon={<Icons.Check/>} label={t.delivered} value={s.total_delivered || 0} color="#10b981" subtext={`${s.delivery_rate || 0}%`}/>
                    <StatCard icon={<Icons.Eye/>} label={t.read} value={s.total_read || 0} color="#3b82f6" subtext={`${s.read_rate || 0}%`}/>
                    <StatCard icon={<Icons.Contacts/>} label={t.contacts} value={s.total_contacts || 0} color="#8b5cf6"/>
                    <StatCard icon={<Icons.Template/>} label={t.approvedTemplates} value={`${s.approved_templates || 0} / ${s.total_templates || 0}`} color="#f59e0b"/>
                </div>

                <h3 style={{ color: 'var(--text-primary, #fff)', marginBottom: '16px', direction: dir }}>{t.recentCampaigns}</h3>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', direction: dir }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid var(--border-primary, rgba(99,102,241,0.15))' }}>
                                {[t.thCampaign, t.thTemplate, t.thStatus, t.thSent, t.thDelivered, t.thRead, t.thFailed, t.thDate].map(h => (
                                    <th key={h} style={{ padding: '12px', textAlign: isRTL ? 'right' : 'left', color: 'var(--text-tertiary, #6b7280)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {(dashboardData?.recent_campaigns || []).map(c => (
                                <tr key={c.id} style={{ borderBottom: '1px solid var(--border-secondary, rgba(99,102,241,0.08))' }}>
                                    <td style={{ padding: '12px', color: 'var(--text-primary, #fff)', fontWeight: 500 }}>{c.name}</td>
                                    <td style={{ padding: '12px', color: 'var(--text-secondary, #9ca3af)', fontSize: '13px' }}>{c.template_name}</td>
                                    <td style={{ padding: '12px' }}><StatusBadge status={c.status} t={t}/></td>
                                    <td style={{ padding: '12px', color: 'var(--text-primary, #fff)' }}>{c.sent_count}</td>
                                    <td style={{ padding: '12px', color: '#10b981' }}>{c.delivered_count}</td>
                                    <td style={{ padding: '12px', color: '#3b82f6' }}>{c.read_count}</td>
                                    <td style={{ padding: '12px', color: '#f87171' }}>{c.failed_count}</td>
                                    <td style={{ padding: '12px', color: 'var(--text-tertiary, #6b7280)', fontSize: '13px' }}>{new Date(c.created_at).toLocaleDateString(dateLocale)}</td>
                                </tr>
                            ))}
                            {(!dashboardData?.recent_campaigns?.length) && (
                                <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary, #6b7280)' }}>{t.noCampaignsYet}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // ============================================================
    // CONTACTS TAB
    // ============================================================
    const renderContacts = () => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', direction: dir, flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ color: 'var(--text-primary, #fff)', margin: 0 }}>{t.contactLists}</h3>
                <div style={{ display: 'flex', gap: '12px' }}>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".xlsx,.xls,.csv" style={{ display: 'none' }} />
                <button style={btnPrimary} onClick={() => fileInputRef.current?.click()} disabled={loading}>
                    <Icons.Upload/> {loading ? t.uploading : t.uploadExcel}
                </button>
                <button style={{
                    background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                    color: '#fff', border: 'none', borderRadius: '10px',
                    padding: '12px 24px', fontSize: '14px', fontWeight: 600,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px',
                    transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(37,211,102,0.3)'
                }} onClick={() => navigate('/marketing/whatsapp-import')}>
                    <Icons.WhatsApp/> {t.importFromWhatsApp}
                </button>
            </div>
                
            </div>

            {/* Upload result */}
            {uploadResult && (
                <div style={{
                    background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                    borderRadius: '12px', padding: '20px', marginBottom: '24px', direction: dir
                }}>
                    <h4 style={{ color: '#34d399', margin: '0 0 12px 0' }}>{t.importedSuccess}</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
                        <div><span style={{ color: 'var(--text-tertiary, #6b7280)' }}>{t.total}</span> <strong style={{ color: 'var(--text-primary, #fff)' }}>{uploadResult.stats.total}</strong></div>
                        <div><span style={{ color: 'var(--text-tertiary, #6b7280)' }}>{t.valid}</span> <strong style={{ color: '#34d399' }}>{uploadResult.stats.valid}</strong></div>
                        <div><span style={{ color: 'var(--text-tertiary, #6b7280)' }}>{t.invalid}</span> <strong style={{ color: '#f87171' }}>{uploadResult.stats.invalid}</strong></div>
                        <div><span style={{ color: 'var(--text-tertiary, #6b7280)' }}>{t.duplicate}</span> <strong style={{ color: '#fbbf24' }}>{uploadResult.stats.duplicates}</strong></div>
                    </div>
                </div>
            )}

            {/* Contact Lists Table */}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', direction: dir }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-primary, rgba(99,102,241,0.15))' }}>
                            {[t.thListName, t.thTotal, t.thValid, t.thInvalid, t.thDuplicate, t.thDate, t.thActions].map(h => (
                                <th key={h} style={{ padding: '12px', textAlign: isRTL ? 'right' : 'left', color: 'var(--text-tertiary, #6b7280)', fontSize: '12px', fontWeight: 600 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {contactLists.map(list => (
                            <tr key={list.id} style={{ borderBottom: '1px solid var(--border-secondary, rgba(99,102,241,0.08))' }}>
                                <td style={{ padding: '12px', color: 'var(--text-primary, #fff)', fontWeight: 500 }}>{list.name}</td>
                                <td style={{ padding: '12px', color: 'var(--text-primary, #fff)' }}>{list.total_contacts}</td>
                                <td style={{ padding: '12px', color: '#34d399' }}>{list.valid_contacts}</td>
                                <td style={{ padding: '12px', color: '#f87171' }}>{list.invalid_contacts}</td>
                                <td style={{ padding: '12px', color: '#fbbf24' }}>{list.duplicate_contacts}</td>
                                <td style={{ padding: '12px', color: 'var(--text-tertiary, #6b7280)', fontSize: '13px' }}>{new Date(list.created_at).toLocaleDateString(dateLocale)}</td>
                                <td style={{ padding: '12px' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button style={{ ...btnSecondary, padding: '6px 12px', fontSize: '12px' }} onClick={() => viewListContacts(list)}><Icons.Eye/></button>
                                        <button style={{ ...btnDanger, padding: '6px 12px', fontSize: '12px' }} onClick={() => deleteList(list.id)}><Icons.Trash/></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!contactLists.length && (
                            <tr><td colSpan={7} style={{ padding: '60px', textAlign: 'center', color: 'var(--text-tertiary, #6b7280)' }}>
                                <Icons.Upload/><br/><br/>{t.uploadExcelToAdd}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* View Contacts Modal */}
            <Modal isOpen={!!selectedList} onClose={() => { setSelectedList(null); setListContacts([]); setSelectedContacts(new Set()); setEditingContact(null); setContactSearch(''); }} title={`${t.contactsOf} ${selectedList?.name}`} width="1000px">
                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap', direction: dir }}>
                    <input value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder={t.searchByNameOrPhone}
                        style={{ flex: 1, minWidth: 200, padding: '8px 14px', border: '1.5px solid var(--border-primary)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', direction: dir }} />
                    {selectedContacts.size > 0 && (
                        <button onClick={bulkDeleteContacts} style={{ ...btnDanger, padding: '8px 16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Icons.Trash/> {t.deleteSelected} ({selectedContacts.size})
                        </button>
                    )}
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {contactPagination ? `${contactPagination.total} ${t.numberCount}` : `${filteredListContacts.length} ${t.numberCount}`}
                    </span>
                </div>

                <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', direction: dir }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid var(--border-primary)', position: 'sticky', top: 0, background: 'var(--bg-primary)', zIndex: 1 }}>
                                <th style={{ padding: '10px', width: 40 }}><input type="checkbox" checked={selectedContacts.size > 0 && selectedContacts.size === filteredListContacts.length} onChange={toggleAllContacts} style={{ width: 16, height: 16, cursor: 'pointer' }} /></th>
                                <th style={{ padding: '10px', textAlign: isRTL ? 'right' : 'left', color: 'var(--text-tertiary)', fontSize: '12px', width: 40 }}>#</th>
                                <th style={{ padding: '10px', textAlign: isRTL ? 'right' : 'left', color: 'var(--text-tertiary)', fontSize: '12px' }}>{t.thName}</th>
                                <th style={{ padding: '10px', textAlign: isRTL ? 'right' : 'left', color: 'var(--text-tertiary)', fontSize: '12px' }}>{t.thWhatsappNumber}</th>
                                <th style={{ padding: '10px', textAlign: isRTL ? 'right' : 'left', color: 'var(--text-tertiary)', fontSize: '12px' }}>{t.thCompany}</th>
                                <th style={{ padding: '10px', textAlign: isRTL ? 'right' : 'left', color: 'var(--text-tertiary)', fontSize: '12px', width: 70 }}>{t.thStatus}</th>
                                <th style={{ padding: '10px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '12px', width: 100 }}>{t.thActions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredListContacts.map((c, i) => (
                                <tr key={c.id} style={{ borderBottom: '1px solid var(--border-secondary)', background: selectedContacts.has(c.id) ? 'rgba(99,102,241,0.06)' : 'transparent' }}>
                                    <td style={{ padding: '8px 10px' }}><input type="checkbox" checked={selectedContacts.has(c.id)} onChange={() => toggleContactSelect(c.id)} style={{ width: 16, height: 16, cursor: 'pointer' }} /></td>
                                    <td style={{ padding: '8px 10px', color: 'var(--text-tertiary)', fontSize: '12px' }}>{((contactPage - 1) * 50) + i + 1}</td>
                                    <td style={{ padding: '8px 10px' }}>
                                        {editingContact === c.id ? (
                                            <input defaultValue={c.name} onBlur={e => updateContact(c.id, { name: e.target.value })} onKeyDown={e => e.key === 'Enter' && updateContact(c.id, { name: e.target.value })} autoFocus
                                                style={{ width: '100%', padding: '4px 8px', border: '1.5px solid #6366f1', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', direction: dir }} />
                                        ) : (
                                            <span style={{ color: 'var(--text-primary)', cursor: 'pointer' }} onDoubleClick={() => setEditingContact(c.id)}>{c.name || '—'}</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px 10px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 12, direction: 'ltr', textAlign: 'left' }}>{c.phone}</td>
                                    <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{c.company_name || '—'}</td>
                                    <td style={{ padding: '8px 10px' }}>
                                        {c.phone_valid ? <span style={{ color: '#34d399', fontSize: '12px' }}>{t.validStatus}</span> : <span style={{ color: '#f87171', fontSize: '12px' }}>{t.invalidStatus}</span>}
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                        <button onClick={() => setEditingContact(editingContact === c.id ? null : c.id)} title={t.edit}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', padding: '4px', fontSize: 14 }}>✏️</button>
                                        <button onClick={() => deleteContact(c.id)} title={t.delete}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px', fontSize: 14 }}>🗑️</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!filteredListContacts.length && <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)' }}>{t.noResults}</div>}
                </div>

                {/* Pagination */}
                {contactPagination && contactPagination.pages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 14, direction: 'ltr' }}>
                        <button disabled={contactPage <= 1} onClick={() => viewListContacts(selectedList, contactPage - 1)}
                            style={{ padding: '6px 14px', border: '1px solid var(--border-primary)', borderRadius: 6, background: 'var(--bg-primary)', cursor: 'pointer', fontSize: 13 }}>{t.previous}</button>
                        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t.pageOf.replace('{page}', contactPage).replace('{total}', contactPagination.pages)}</span>
                        <button disabled={contactPage >= contactPagination.pages} onClick={() => viewListContacts(selectedList, contactPage + 1)}
                            style={{ padding: '6px 14px', border: '1px solid var(--border-primary)', borderRadius: 6, background: 'var(--bg-primary)', cursor: 'pointer', fontSize: 13 }}>{t.next}</button>
                    </div>
                )}
            </Modal>
        </div>
    );

    // ============================================================
    // TEMPLATES TAB
    // ============================================================
    const renderTemplates = () => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', direction: dir, flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ color: 'var(--text-primary, #fff)', margin: 0 }}>{t.whatsappTemplates}</h3>
                <div style={{ display: 'flex', gap: '12px' }}>
                {(userRole === "admin" || userRole === "supervisor") && <>
                    <button style={btnSecondary} onClick={syncTemplates} disabled={syncing}>
                        <Icons.Sync style={syncing ? { animation: 'spin 1s linear infinite' } : {}}/> {syncing ? t.syncing : t.syncFromMeta}
                    </button>
                    <button style={btnPrimary} onClick={() => setShowTemplateModal(true)}>
                        <Icons.Plus/> {t.createNewTemplate}
                    </button>
                </>}
                </div>
            </div>

            {/* Templates Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
                {templates.map(tmpl => (
                    <div key={tmpl.id} style={{
                        background: 'var(--bg-card, rgba(26,32,56,0.6))',
                        border: '1px solid var(--border-primary, rgba(99,102,241,0.15))',
                        borderRadius: '16px', padding: '20px', direction: dir,
                        transition: 'all 0.2s'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <div>
                                <div style={{ color: 'var(--text-primary, #fff)', fontWeight: 600, marginBottom: '4px' }}>{tmpl.display_name || tmpl.meta_template_name}</div>
                                <div style={{ color: 'var(--text-tertiary, #6b7280)', fontSize: '12px', fontFamily: 'monospace' }}>{tmpl.meta_template_name}</div>
                            </div>
                            <StatusBadge status={tmpl.status} t={t}/>
                        </div>
                        
                        <div style={{ 
                            background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '12px', marginBottom: '12px',
                            color: 'var(--text-secondary, #9ca3af)', fontSize: '13px', lineHeight: 1.6,
                            whiteSpace: 'pre-wrap', maxHeight: '120px', overflow: 'auto'
                        }}>
                            {tmpl.body_text || t.noBodyText}
                        </div>

                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                            <span style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(99,102,241,0.1)', color: '#818cf8' }}>
                                {tmpl.language === 'ar' ? t.arabicLang : t.englishLang}
                            </span>
                            <span style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(99,102,241,0.1)', color: '#818cf8' }}>
                                {tmpl.category}
                            </span>
                            {tmpl.header_type !== 'NONE' && (
                                <span style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(245,158,11,0.1)', color: '#fbbf24' }}>
                                    Header: {tmpl.header_type}
                                </span>
                            )}
                            {tmpl.has_buttons ? (
                                <span style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(16,185,129,0.1)', color: '#34d399' }}>
                                    {t.buttonsYes}
                                </span>
                            ) : null}
                            <span style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(139,92,246,0.1)', color: '#a78bfa' }}>
                                {tmpl.body_params_count} {t.paramCount}
                            </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button style={{ ...btnDanger, padding: '6px 12px', fontSize: '12px' }} onClick={() => deleteTemplate(tmpl.id)}>
                                <Icons.Trash/> {t.delete}
                            </button>
                        </div>
                    </div>
                ))}
                {!templates.length && (
                    <div style={{ gridColumn: '1/-1', padding: '60px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                        <Icons.Template/><br/><br/>{t.noTemplates}
                    </div>
                )}
            </div>

            {/* Create Template Modal */}
            <Modal isOpen={showTemplateModal} onClose={() => setShowTemplateModal(false)} title={t.createTemplateTitle} width="700px">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', direction: dir }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                        <div>
                            <label style={labelStyle}>{t.templateName}</label>
                            <input style={inputStyle} placeholder={t.templateNamePlaceholder} value={templateForm.name}
                                onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') }))}
                                dir="ltr"/>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>{t.templateNameHint}</div>
                        </div>
                        <div>
                            <label style={labelStyle}>{t.language}</label>
                            <select style={inputStyle} value={templateForm.language} onChange={e => setTemplateForm(p => ({ ...p, language: e.target.value }))}>
                                <option value="ar">{t.langArabic}</option>
                                <option value="en">{t.langEnglish}</option>
                                <option value="en_US">{t.langEnglishUS}</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t.category}</label>
                            <select style={inputStyle} value={templateForm.category} onChange={e => setTemplateForm(p => ({ ...p, category: e.target.value }))}>
                                <option value="MARKETING">{t.catMarketing}</option>
                                <option value="UTILITY">{t.catUtility}</option>
                            </select>
                        </div>
                    </div>

                    {/* Header */}
                    <div>
                        <label style={labelStyle}>{t.headerType}</label>
                        <select style={inputStyle} value={templateForm.header_type} onChange={e => setTemplateForm(p => ({ ...p, header_type: e.target.value }))}>
                            <option value="NONE">{t.headerNone}</option>
                            <option value="TEXT">{t.headerText}</option>
                            <option value="IMAGE">{t.headerImage}</option>
                            <option value="VIDEO">{t.headerVideo}</option>
                            <option value="DOCUMENT">{t.headerDocument}</option>
                        </select>
                        {templateForm.header_type === 'TEXT' && (
                            <input style={{ ...inputStyle, marginTop: '8px' }} placeholder={t.headerTextPlaceholder} value={templateForm.header_text}
                                onChange={e => setTemplateForm(p => ({ ...p, header_text: e.target.value }))} maxLength={60}/>
                        )}
                        {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(templateForm.header_type) && (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                                <input style={{ ...inputStyle, flex: 1, direction: 'ltr' }} placeholder={t.mediaUrlPlaceholder} value={templateForm.header_example_url}
                                    onChange={e => setTemplateForm(p => ({ ...p, header_example_url: e.target.value }))}/>
                                <label style={{
                                    padding: '10px 16px', borderRadius: '8px', cursor: 'pointer',
                                    background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white',
                                    fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap',
                                    display: 'flex', alignItems: 'center', gap: '6px'
                                }}>
                                    {t.uploadBtn}
                                    <input type="file" accept="image/*,video/mp4,application/pdf" style={{ display: 'none' }}
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            if (file.size > 5 * 1024 * 1024) { showToast(t.maxSize5MB, 'error'); return; }
                                            const fd = new FormData();
                                            fd.append('file', file);
                                            try {
                                                const res = await fetch('/api/v1/marketing/upload-media', {
                                                    method: 'POST',
                                                    headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },
                                                    body: fd
                                                });
                                                const data = await res.json();
                                                if (data.success) {
                                                    setTemplateForm(p => ({ ...p, header_example_url: data.url }));
                                                    showToast(t.fileUploaded, 'success');
                                                } else {
                                                    showToast(data.message || t.uploadFailed, 'error');
                                                }
                                            } catch (err) {
                                                showToast(t.uploadError, 'error');
                                            }
                                            e.target.value = '';
                                        }}/>
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Body */}
                    <div>
                        <label style={labelStyle}>{t.messageBody}</label>
                        <textarea style={{ ...inputStyle, minHeight: '120px', resize: 'vertical', direction: dir }} value={templateForm.body_text}
                            onChange={e => setTemplateForm(p => ({ ...p, body_text: e.target.value }))}
                            placeholder={t.messageBodyPlaceholder}/>
                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                            {(templateForm.body_text.match(/\{\{\d+\}\}/g) || []).length} {t.paramCount}
                        </div>
                    </div>

                    {/* Body Examples */}
                    {(templateForm.body_text.match(/\{\{\d+\}\}/g) || []).length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={labelStyle}>{t.exampleValues}</label>
                                <input style={inputStyle} placeholder={t.exampleValuesPlaceholder} value={templateForm.body_example_values}
                                    onChange={e => setTemplateForm(p => ({ ...p, body_example_values: e.target.value }))}/>
                            </div>
                            <div>
                                <label style={labelStyle}>{t.paramLabels}</label>
                                <input style={inputStyle} placeholder={t.paramLabelsPlaceholder} value={templateForm.body_param_labels}
                                    onChange={e => setTemplateForm(p => ({ ...p, body_param_labels: e.target.value }))}/>
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div>
                        <label style={labelStyle}>{t.footerText}</label>
                        <input style={inputStyle} placeholder={t.footerPlaceholder} value={templateForm.footer_text}
                            onChange={e => setTemplateForm(p => ({ ...p, footer_text: e.target.value }))} maxLength={60}/>
                    </div>

                    {/* Buttons */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ ...labelStyle, margin: 0 }}>{t.buttonsLabel}</label>
                            {templateForm.buttons.length < 3 && (
                                <button style={{ ...btnSecondary, padding: '6px 12px', fontSize: '12px' }} onClick={addButton}>
                                    <Icons.Plus/> {t.addButton}
                                </button>
                            )}
                        </div>
                        {templateForm.buttons.map((btn, i) => (
                            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                <select style={{ ...inputStyle, width: 'auto', minWidth: '140px' }} value={btn.type}
                                    onChange={e => updateButton(i, 'type', e.target.value)}>
                                    <option value="QUICK_REPLY">{t.quickReply}</option>
                                    <option value="URL">{t.urlType}</option>
                                    <option value="PHONE_NUMBER">{t.phoneType}</option>
                                </select>
                                <input style={inputStyle} placeholder={t.buttonTextPlaceholder} value={btn.text}
                                    onChange={e => updateButton(i, 'text', e.target.value)}/>
                                {btn.type === 'URL' && (
                                    <input style={{ ...inputStyle, direction: 'ltr' }} placeholder="https://..." value={btn.url || ''}
                                        onChange={e => updateButton(i, 'url', e.target.value)}/>
                                )}
                                {btn.type === 'PHONE_NUMBER' && (
                                    <input style={{ ...inputStyle, direction: 'ltr' }} placeholder="+201..." value={btn.phone_number || ''}
                                        onChange={e => updateButton(i, 'phone_number', e.target.value)}/>
                                )}
                                <button style={{ ...btnDanger, padding: '8px' }} onClick={() => removeButton(i)}><Icons.Close/></button>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid var(--border-primary)' }}>
                        <button style={btnSecondary} onClick={() => setShowTemplateModal(false)}>{t.cancel}</button>
                        <button style={btnPrimary} onClick={createTemplate} disabled={loading}>
                            {loading ? t.creating : t.createAndSubmit}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );

    // ============================================================
    // CAMPAIGNS TAB
    // ============================================================
    const renderCampaigns = () => {
        const selectedTemplateData = templates.find(t => t.id === Number(campaignForm.template_id));

        return (
            <div>
                {!selectedCampaign ? (<>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', direction: dir, flexWrap: 'wrap', gap: '12px' }}>
                    <h3 style={{ color: 'var(--text-primary, #fff)', margin: 0 }}>{t.marketingCampaigns}</h3>
                    <button style={btnPrimary} onClick={() => setShowCampaignModal(true)}>
                        <Icons.Plus/> {t.newCampaign}
                    </button>
                </div>

                {/* Campaigns List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {campaigns.map(c => (
                        <div key={c.id} style={{
                            background: 'var(--bg-card, rgba(26,32,56,0.6))',
                            border: '1px solid var(--border-primary, rgba(99,102,241,0.15))',
                            borderRadius: '16px', padding: '20px', direction: dir
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                                <div style={{ flex: 1, minWidth: '200px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                        <span style={{ color: 'var(--text-primary, #fff)', fontWeight: 600, fontSize: '16px' }}>{c.name}</span>
                                        <StatusBadge status={c.status} t={t}/>
                                    </div>
                                    <div style={{ color: 'var(--text-tertiary, #6b7280)', fontSize: '13px' }}>
                                        {t.templateLabel} {c.template_name} | {t.listLabel} {c.list_name} ({c.list_size} {t.contactUnit})
                                    </div>
                                    {c.auto_paused_reason && (
                                        <div style={{ color: '#fb923c', fontSize: '13px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Icons.Warning/> {c.auto_paused_reason}
                                        </div>
                                    )}
                                </div>

                                {/* Progress Stats */}
                                <div style={{ display: 'flex', gap: '20px', fontSize: '13px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: 'var(--text-tertiary)', marginBottom: '2px' }}>{t.sent}</div>
                                        <div style={{ color: '#6366f1', fontWeight: 700, fontSize: '18px' }}>{c.sent_count}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: 'var(--text-tertiary)', marginBottom: '2px' }}>{t.delivered}</div>
                                        <div style={{ color: '#10b981', fontWeight: 700, fontSize: '18px' }}>{c.delivered_count}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: 'var(--text-tertiary)', marginBottom: '2px' }}>{t.read}</div>
                                        <div style={{ color: '#3b82f6', fontWeight: 700, fontSize: '18px' }}>{c.read_count}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: 'var(--text-tertiary)', marginBottom: '2px' }}>{t.failed}</div>
                                        <div style={{ color: '#f87171', fontWeight: 700, fontSize: '18px' }}>{c.failed_count}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Progress bar */}
                            {c.total_recipients > 0 && (
                                <div style={{ marginTop: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                                        <span>{c.sent_count + c.failed_count + c.blocked_count} / {c.total_recipients}</span>
                                        <span>{Math.round(((c.sent_count + c.failed_count) / c.total_recipients) * 100)}%</span>
                                    </div>
                                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
                                        <div style={{
                                            height: '100%', borderRadius: '3px',
                                            width: `${Math.round(((c.sent_count + c.failed_count) / c.total_recipients) * 100)}%`,
                                            background: c.status === 'completed' ? 'linear-gradient(90deg, #10b981, #34d399)' :
                                                       c.status === 'failed' ? 'linear-gradient(90deg, #ef4444, #f87171)' :
                                                       'linear-gradient(90deg, #6366f1, #818cf8)',
                                            transition: 'width 0.5s ease'
                                        }}/>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                                {c.status === 'draft' && (
                                    <button style={{ ...btnSuccess, padding: '8px 16px', fontSize: '13px' }} onClick={() => campaignAction(c.id, 'start')}>
                                        <Icons.Play/> {t.startSending}
                                    </button>
                                )}
                                {c.status === 'sending' && (
                                    <button style={{ ...btnWarning, padding: '8px 16px', fontSize: '13px' }} onClick={() => campaignAction(c.id, 'pause')}>
                                        <Icons.Pause/> {t.pauseCampaign}
                                    </button>
                                )}
                                {c.status === 'paused' && (
                                    <>
                                        <button style={{ ...btnSuccess, padding: '8px 16px', fontSize: '13px' }} onClick={() => campaignAction(c.id, 'resume')}>
                                            <Icons.Play/> {t.resumeCampaign}
                                        </button>
                                        <button style={{ ...btnDanger, padding: '8px 16px', fontSize: '13px' }} onClick={() => campaignAction(c.id, 'cancel')}>
                                            <Icons.Stop/> {t.cancelCampaign}
                                        </button>
                                    </>
                                )}
                                <button style={{ ...btnSecondary, padding: '8px 16px', fontSize: '13px' }} onClick={() => viewCampaignDetail(c)}>
                                    <Icons.Eye/> {t.details}
                                </button>
                                {c.status !== 'sending' && (
                                    <button style={{ ...btnDanger, padding: '8px 16px', fontSize: '13px' }} onClick={() => deleteCampaign(c.id, c.status)}>
                                        🗑️ {t.deleteCampaign}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                    {!campaigns.length && (
                        <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-tertiary)', background: 'var(--bg-card, rgba(26,32,56,0.6))', borderRadius: '16px', border: '1px solid var(--border-primary)' }}>
                            <Icons.Campaign/><br/><br/>{t.noCampaignsCreate}
                        </div>
                    )}
                </div>
                </>) : (
                    /* Inline Campaign Detail View */
                    <div style={{ direction: dir }}>
                        <button style={{ ...btnSecondary, marginBottom: '20px', padding: '8px 20px', fontSize: '14px' }} onClick={() => { setSelectedCampaign(null); setCampaignDetail(null); setCampaignMessages([]); }}>
                            {t.backToCampaigns}
                        </button>
                        <h3 style={{ color: 'var(--text-primary)', margin: '0 0 20px' }}>{t.detailsTitle} {selectedCampaign?.name}</h3>

                        {/* Campaign Info */}
                        <div style={{ background: 'var(--bg-card, rgba(26,32,56,0.6))', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid var(--border-primary)' }}>
                            <h4 style={{ color: 'var(--text-primary)', marginTop: 0, marginBottom: '12px' }}>{t.campaignInfo}</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                                <div><span style={{ color: 'var(--text-tertiary)' }}>{t.templateUsed}: </span><span style={{ color: 'var(--text-primary)' }}>{campaignDetail?.campaign?.template_name || campaignDetail?.campaign?.meta_template_name}</span></div>
                                <div><span style={{ color: 'var(--text-tertiary)' }}>{t.contactList}: </span><span style={{ color: 'var(--text-primary)' }}>{campaignDetail?.campaign?.list_name}</span></div>
                                <div><span style={{ color: 'var(--text-tertiary)' }}>{t.createdBy}: </span><span style={{ color: 'var(--text-primary)' }}>{campaignDetail?.campaign?.created_by_name}</span></div>
                                <div><span style={{ color: 'var(--text-tertiary)' }}>{t.totalRecipients}: </span><span style={{ color: 'var(--text-primary)' }}>{campaignDetail?.campaign?.total_recipients}</span></div>
                                {campaignDetail?.campaign?.started_at && <div><span style={{ color: 'var(--text-tertiary)' }}>{t.startedAt}: </span><span style={{ color: 'var(--text-primary)' }}>{new Date(campaignDetail.campaign.started_at).toLocaleString(dateLocale)}</span></div>}
                                {campaignDetail?.campaign?.completed_at && <div><span style={{ color: 'var(--text-tertiary)' }}>{t.completedAt}: </span><span style={{ color: 'var(--text-primary)' }}>{new Date(campaignDetail.campaign.completed_at).toLocaleString(dateLocale)}</span></div>}
                            </div>
                        </div>

                        {/* Stats - clickable to filter */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px', marginBottom: '20px' }}>
                            {(() => {
                                const stats = campaignDetail?.message_stats || [];
                                const getCount = (st) => (stats.find(s => s.status === st)?.count || 0);
                                const deliveredCount = getCount('delivered') + getCount('read');
                                const readCount = getCount('read');
                                const failedCount = getCount('failed');
                                const sentCount = getCount('sent') + deliveredCount + failedCount;
                                const cards = [
                                    { status: 'sent', count: sentCount, color: 'var(--text-primary)', label: t.sent || 'sent' },
                                    { status: 'delivered', count: deliveredCount, color: '#10b981', label: t.delivered || 'delivered' },
                                    { status: 'read', count: readCount, color: '#3b82f6', label: t.read || 'read' },
                                    { status: 'failed', count: failedCount, color: '#ef4444', label: t.failed || 'failed' },
                                ];
                                return cards.filter(c => c.count > 0).map(c => (
                                    <div key={c.status} style={{
                                        textAlign: 'center', padding: '14px', borderRadius: '12px', cursor: 'pointer',
                                        background: messageFilter === c.status ? 'rgba(99,102,241,0.15)' : 'var(--bg-card, rgba(26,32,56,0.6))',
                                        border: messageFilter === c.status ? '2px solid #6366f1' : '1px solid var(--border-primary)'
                                    }} onClick={() => setMessageFilter(messageFilter === c.status ? '' : c.status)}>
                                        <div style={{ fontSize: '22px', fontWeight: 700, color: c.color }}>{c.count}</div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{c.label}</div>
                                    </div>
                                ));
                            })()}
                        </div>

                        {/* Recipients Table */}
                        <div style={{ background: 'var(--bg-card, rgba(26,32,56,0.6))', borderRadius: '12px', border: '1px solid var(--border-primary)', marginBottom: '20px' }}>
                            <h4 style={{ color: 'var(--text-primary)', padding: '16px 20px 0', margin: 0 }}>{t.recipientsList}</h4>
                            <div style={{ maxHeight: '400px', overflow: 'auto', padding: '0 4px 4px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ background: 'rgba(255,255,255,0.05)', position: 'sticky', top: 0, zIndex: 1 }}>
                                            <th style={{ padding: '12px', textAlign: dir === 'rtl' ? 'right' : 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>#</th>
                                            <th style={{ padding: '12px', textAlign: dir === 'rtl' ? 'right' : 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>{t.phoneNumber}</th>
                                            <th style={{ padding: '12px', textAlign: dir === 'rtl' ? 'right' : 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>{t.contactName}</th>
                                            <th style={{ padding: '12px', textAlign: 'center', color: '#f59e0b', fontWeight: 600 }}>📤 {isRTL ? 'مرسل' : 'Sent'}</th>
                                            <th style={{ padding: '12px', textAlign: 'center', color: '#10b981', fontWeight: 600 }}>✅ {isRTL ? 'وصل' : 'Delivered'}</th>
                                            <th style={{ padding: '12px', textAlign: 'center', color: '#3b82f6', fontWeight: 600 }}>👁 {isRTL ? 'مقروء' : 'Read'}</th>
                                            <th style={{ padding: '12px', textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>❌ {isRTL ? 'فشل' : 'Failed'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaignMessages.filter(m => !messageFilter || m.status === messageFilter).map((m, i) => {
                                            const statusOrder = ['sent', 'delivered', 'read'];
                                            const statusIdx = statusOrder.indexOf(m.status);
                                            const isSent = statusIdx >= 0;
                                            const isDelivered = statusIdx >= 1;
                                            const isRead = statusIdx >= 2;
                                            const isFailed = m.status === 'failed';
                                            const checkStyle = { fontSize: '16px', color: '#10b981' };
                                            const crossStyle = { fontSize: '16px', color: '#ef4444' };
                                            const dashStyle = { fontSize: '14px', color: 'var(--text-tertiary)' };
                                            return (
                                                <tr key={m.id} style={{ borderBottom: '1px solid var(--border-secondary, rgba(255,255,255,0.05))' }}>
                                                    <td style={{ padding: '10px 12px', color: 'var(--text-tertiary)' }}>{i + 1}</td>
                                                    <td style={{ padding: '10px 12px', color: 'var(--text-primary)', direction: 'ltr', fontFamily: 'monospace' }}>{m.phone}</td>
                                                    <td style={{ padding: '10px 12px', color: 'var(--text-primary)' }}>{m.contact_name || '—'}</td>
                                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><span style={isSent ? checkStyle : dashStyle}>{isSent ? '✓' : '—'}</span></td>
                                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><span style={isDelivered ? checkStyle : dashStyle}>{isDelivered ? '✓' : '—'}</span></td>
                                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><span style={isRead ? checkStyle : dashStyle}>{isRead ? '✓' : '—'}</span></td>
                                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><span style={isFailed ? crossStyle : dashStyle}>{isFailed ? '✗' : '—'}</span></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Activity Log */}
                        <div style={{ background: 'var(--bg-card, rgba(26,32,56,0.6))', borderRadius: '12px', border: '1px solid var(--border-primary)' }}>
                            <h4 style={{ color: 'var(--text-primary)', padding: '16px 20px 0', margin: 0 }}>{t.activityLog}</h4>
                            <div style={{ padding: '12px 20px 16px' }}>
                                {(campaignDetail?.activities || []).map(a => (
                                    <div key={a.id} style={{
                                        display: 'flex', justifyContent: 'space-between', padding: '10px 0',
                                        borderBottom: '1px solid var(--border-secondary, rgba(255,255,255,0.05))', fontSize: '13px'
                                    }}>
                                        <span style={{ color: 'var(--text-primary)' }}>{a.details}</span>
                                        <span style={{ color: 'var(--text-tertiary)', fontSize: '12px', whiteSpace: 'nowrap', marginInlineStart: '12px' }}>
                                            {new Date(a.created_at).toLocaleString(dateLocale)}
                                            {a.performed_by_name && ` - ${a.performed_by_name}`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Create Campaign Modal */}
                <Modal isOpen={showCampaignModal} onClose={() => setShowCampaignModal(false)} title={t.createCampaignTitle} width="750px">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', direction: dir }}>
                        <div>
                            <label style={labelStyle}>{t.campaignName}</label>
                            <input style={inputStyle} placeholder={t.campaignNamePlaceholder} value={campaignForm.name}
                                onChange={e => setCampaignForm(p => ({ ...p, name: e.target.value }))}/>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={labelStyle}>{t.selectTemplate}</label>
                                <select style={inputStyle} value={campaignForm.template_id}
                                    onChange={e => setCampaignForm(p => ({ ...p, template_id: e.target.value }))}>
                                    <option value="">{t.selectTemplatePlaceholder}</option>
                                    {templates.filter(tmpl => tmpl.status === 'APPROVED').map(tmpl => (
                                        <option key={tmpl.id} value={tmpl.id}>{tmpl.display_name} ({tmpl.language})</option>
                                    ))}
                                </select>
                                {templates.filter(tmpl => tmpl.status === 'APPROVED').length === 0 && (
                                    <div style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>{t.noApprovedTemplates}</div>
                                )}
                            </div>
                            <div>
                                <label style={labelStyle}>{t.selectContactList}</label>
                                <select style={inputStyle} value={campaignForm.contact_list_id}
                                    onChange={e => setCampaignForm(p => ({ ...p, contact_list_id: e.target.value }))}>
                                    <option value="">{t.selectListPlaceholder}</option>
                                    {contactLists.map(l => (
                                        <option key={l.id} value={l.id}>{l.name} ({l.valid_contacts} {t.contactUnit})</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Notify Agents */}
                        <div style={{ marginTop: "16px" }}>
                            <label style={labelStyle}>{t.notifyAgents}</label>
                            <p style={{ fontSize: "12px", color: "#9CA3AF", margin: "4px 0 8px" }}>{t.notifyAgentsDesc}</p>
                            <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #374151", borderRadius: "8px", padding: "8px" }}>
                                {agentsList.filter(a => a.phone_whatsapp).map(agent => (
                                    <label key={agent.id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 8px", cursor: "pointer", borderRadius: "6px" }}>
                                        <input type="checkbox" checked={campaignForm.notify_agents.includes(agent.id)}
                                            onChange={e => {
                                                const id = agent.id;
                                                setCampaignForm(p => ({
                                                    ...p,
                                                    notify_agents: e.target.checked ? [...p.notify_agents, id] : p.notify_agents.filter(x => x !== id)
                                                }));
                                            }} style={{ width: "16px", height: "16px" }} />
                                        <span style={{ fontSize: "14px" }}>{agent.full_name}</span>
                                        <span style={{ fontSize: "11px", color: "#9CA3AF", marginInlineStart: "auto" }}>{agent.role}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        {/* Header Media URL - for templates with IMAGE/VIDEO/DOCUMENT header */}
                        {selectedTemplateData && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTemplateData.header_type) && (
                            <div style={{ background: 'rgba(16,185,129,0.05)', borderRadius: '12px', padding: '16px', border: '1px solid rgba(16,185,129,0.15)' }}>
                                <label style={{ ...labelStyle, color: '#10b981', marginBottom: '8px' }}>
                                    {selectedTemplateData.header_type === 'IMAGE' ? t.imageLink : 
                                     selectedTemplateData.header_type === 'VIDEO' ? t.videoLink : t.fileLink} *
                                </label>
                                <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '0 0 8px' }}>
                                    {selectedTemplateData.header_type === 'IMAGE' ? t.enterImageUrl : selectedTemplateData.header_type === 'VIDEO' ? t.enterVideoUrl : t.enterFileUrl}
                                </p>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input style={{ ...inputStyle, flex: 1 }} 
                                        placeholder={selectedTemplateData.header_type === 'IMAGE' ? t.imageUrlPlaceholder : t.genericUrlPlaceholder}
                                        value={campaignForm.header_media_url}
                                        onChange={e => setCampaignForm(p => ({ ...p, header_media_url: e.target.value }))}/>
                                    <label style={{
                                        padding: '10px 16px', borderRadius: '8px', cursor: 'pointer',
                                        background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white',
                                        fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap',
                                        display: 'flex', alignItems: 'center', gap: '6px'
                                    }}>
                                        📁 {isRTL ? 'رفع ملف' : 'Upload File'}
                                        <input type="file" accept="image/*,video/mp4,application/pdf" style={{ display: 'none' }}
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                if (file.size > 5 * 1024 * 1024) { showToast(t.maxSize5MB, 'error'); return; }
                                                const fd = new FormData();
                                                fd.append('file', file);
                                                try {
                                                    const res = await fetch('/api/v1/marketing/upload-media', {
                                                        method: 'POST',
                                                        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },
                                                        body: fd
                                                    });
                                                    const data = await res.json();
                                                    if (data.success) {
                                                        setCampaignForm(p => ({ ...p, header_media_url: data.url }));
                                                        showToast(t.fileUploadedSuccess, 'success');
                                                    } else {
                                                        showToast(data.message || t.uploadFailed, 'error');
                                                    }
                                                } catch (err) {
                                                    showToast(t.uploadError, 'error');
                                                }
                                                e.target.value = '';
                                            }}/>
                                    </label>
                                </div>
                                {campaignForm.header_media_url && selectedTemplateData.header_type === 'IMAGE' && (
                                    <div style={{ marginTop: '8px', textAlign: 'center' }}>
                                        <img src={campaignForm.header_media_url} alt="preview" 
                                            style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}
                                            onError={e => { e.target.style.display = 'none'; }}
                                            onLoad={e => { e.target.style.display = 'block'; }}/>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Parameter Mapping */}
                        {selectedTemplateData && selectedTemplateData.body_params_count > 0 && (
                            <div style={{ background: 'rgba(99,102,241,0.05)', borderRadius: '12px', padding: '16px', border: '1px solid rgba(99,102,241,0.15)' }}>
                                <label style={{ ...labelStyle, color: '#818cf8', marginBottom: '12px' }}>{t.paramMapping}</label>
                                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '12px' }}>
                                    {t.templateText} {selectedTemplateData.body_text}
                                </div>
                                {Array.from({ length: selectedTemplateData.body_params_count }, (_, i) => {
                                    const paramLabels = selectedTemplateData.body_params_labels ? JSON.parse(selectedTemplateData.body_params_labels) : [];
                                    const label = paramLabels[i] || t.paramLabel.replace('{n}', i + 1);
                                    return (
                                        <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
                                            <span style={{ minWidth: '120px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                                                {`{{${i + 1}}}`} - {label}:
                                            </span>
                                            <select style={{ ...inputStyle, flex: 1 }}
                                                value={campaignForm.param_mapping[String(i + 1)] || ''}
                                                onChange={e => setCampaignForm(p => ({
                                                    ...p,
                                                    param_mapping: { ...p.param_mapping, [String(i + 1)]: e.target.value }
                                                }))}>
                                                <option value="">{t.selectFieldOrCustom}</option>
                                                <option value="name">{t.customerName}</option>
                                                <option value="company_name">{t.companyName}</option>
                                                <option value="phone">{t.phoneNumber}</option>
                                                <option value="__custom__">{t.customValue}</option>
                                            </select>
                                            {campaignForm.param_mapping[String(i + 1)] === '__custom__' && (
                                                <input style={{ ...inputStyle, flex: 1 }} placeholder={t.enterCustomValue}
                                                    value={campaignForm.custom_params[String(i + 1)] || ''}
                                                    onChange={e => setCampaignForm(p => ({
                                                        ...p,
                                                        custom_params: { ...p.custom_params, [String(i + 1)]: e.target.value }
                                                    }))}/>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Safety Settings */}
                        <div style={{ background: 'rgba(245,158,11,0.05)', borderRadius: '12px', padding: '16px', border: '1px solid rgba(245,158,11,0.15)' }}>
                            <label style={{ ...labelStyle, color: '#fbbf24', marginBottom: '12px' }}>{t.safetySettings}</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                                <div>
                                    <label style={{ ...labelStyle, fontSize: '11px' }}>{t.batchSize}</label>
                                    <input type="number" style={inputStyle} value={campaignForm.batch_size}
                                        onChange={e => setCampaignForm(p => ({ ...p, batch_size: parseInt(e.target.value) || 10 }))}
                                        min={1} max={50}/>
                                </div>
                                <div>
                                    <label style={{ ...labelStyle, fontSize: '11px' }}>{t.batchDelay}</label>
                                    <input type="number" style={inputStyle} value={campaignForm.batch_delay_seconds}
                                        onChange={e => setCampaignForm(p => ({ ...p, batch_delay_seconds: parseInt(e.target.value) || 5 }))}
                                        min={1} max={60}/>
                                </div>
                                <div>
                                    <label style={{ ...labelStyle, fontSize: '11px' }}>{t.maxPerHour}</label>
                                    <input type="number" style={inputStyle} value={campaignForm.max_per_hour}
                                        onChange={e => setCampaignForm(p => ({ ...p, max_per_hour: parseInt(e.target.value) || 200 }))}
                                        min={10} max={1000}/>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input type="checkbox" checked={campaignForm.respect_business_hours}
                                        onChange={e => setCampaignForm(p => ({ ...p, respect_business_hours: e.target.checked }))}
                                        style={{ width: '18px', height: '18px' }}/>
                                    <label style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{t.businessHoursOnly}</label>
                                </div>
                            </div>
                            {campaignForm.respect_business_hours && (
                                <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                                    <div>
                                        <label style={{ ...labelStyle, fontSize: '11px' }}>{t.from}</label>
                                        <input type="time" style={inputStyle} value={campaignForm.business_hours_start}
                                            onChange={e => setCampaignForm(p => ({ ...p, business_hours_start: e.target.value }))}/>
                                    </div>
                                    <div>
                                        <label style={{ ...labelStyle, fontSize: '11px' }}>{t.to}</label>
                                        <input type="time" style={inputStyle} value={campaignForm.business_hours_end}
                                            onChange={e => setCampaignForm(p => ({ ...p, business_hours_end: e.target.value }))}/>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid var(--border-primary)' }}>
                            <button style={btnSecondary} onClick={() => setShowCampaignModal(false)}>{t.cancel}</button>
                            <button style={btnPrimary} onClick={createCampaign} disabled={loading}>
                                {loading ? t.creating : t.createCampaign}
                            </button>
                        </div>
                    </div>
                </Modal>

            </div>
        );
    };

    // ============================================================
    // MAIN RENDER
    // ============================================================
    return (
        <Layout>
        <div className="content-wrapper" style={{ padding: '24px' }}>
            {/* Page Header */}
            <div className="page-header" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '24px', direction: dir, flexWrap: 'wrap', gap: '16px'
            }}>
                <div>
                    <h2 className="page-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ color: '#25d366' }}><Icons.WhatsApp/></span>
                        {t.pageTitle}
                    </h2>
                    <p className="page-subtitle" style={{ margin: '4px 0 0 0' }}>{t.pageSubtitle}</p>
                </div>
            </div>

            {/* Tab Navigation */}
            <div style={{
                display: 'flex', gap: '4px', marginBottom: '24px', direction: dir,
                background: 'var(--bg-card, rgba(26,32,56,0.6))',
                border: '1px solid var(--border-primary, rgba(99,102,241,0.15))',
                borderRadius: '14px', padding: '6px', overflow: 'auto'
            }}>
                {tabs.map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '12px 20px', borderRadius: '10px', border: 'none',
                        fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                        transition: 'all 0.2s', whiteSpace: 'nowrap',
                        background: activeTab === tab.key ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'transparent',
                        color: activeTab === tab.key ? '#fff' : 'var(--text-secondary, #9ca3af)',
                        boxShadow: activeTab === tab.key ? '0 4px 12px rgba(99,102,241,0.3)' : 'none',
                    }}>
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
                {activeTab === 'dashboard' && renderDashboard()}
                {activeTab === 'contacts' && renderContacts()}
                {activeTab === 'templates' && renderTemplates()}
                {activeTab === 'campaigns' && renderCampaigns()}
            </div>

            {/* Toast Notification */}
            {toast && (
                <div style={{
                    position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
                    padding: '14px 28px', borderRadius: '12px', zIndex: 99999,
                    background: toast.type === 'error' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'linear-gradient(135deg, #10b981, #059669)',
                    color: '#fff', fontWeight: 500, fontSize: '14px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    animation: 'fadeIn 0.3s ease-out',
                    direction: dir
                }}>
                    {toast.message}
                </div>
            )}
        </div>
        </Layout>
    );
};

export default Marketing;